import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { haptic } from '../../../shared/utils/haptics';

// Configuration des badges
export const BADGES = {
  // Streaks
  streak_3: { id: 'streak_3', name: 'Flamme naissante', icon: '🔥', description: '3 jours consécutifs', requirement: 3, type: 'streak' },
  streak_7: { id: 'streak_7', name: 'Semaine parfaite', icon: '⭐', description: '7 jours consécutifs', requirement: 7, type: 'streak' },
  streak_14: { id: 'streak_14', name: 'Guerrier', icon: '⚔️', description: '14 jours consécutifs', requirement: 14, type: 'streak' },
  streak_30: { id: 'streak_30', name: 'Diamant de constance', icon: '💎', description: '30 jours consécutifs', requirement: 30, type: 'streak' },
  streak_100: { id: 'streak_100', name: 'Légende', icon: '🏆', description: '100 jours consécutifs', requirement: 100, type: 'streak' },

  // XP totaux
  xp_100: { id: 'xp_100', name: 'Premier pas', icon: '👣', description: '100 XP cumulés', requirement: 100, type: 'xp_total' },
  xp_500: { id: 'xp_500', name: 'En route', icon: '🚀', description: '500 XP cumulés', requirement: 500, type: 'xp_total' },
  xp_1000: { id: 'xp_1000', name: 'Millénaire', icon: '🌟', description: '1000 XP cumulés', requirement: 1000, type: 'xp_total' },
  xp_5000: { id: 'xp_5000', name: 'Maître', icon: '👑', description: '5000 XP cumulés', requirement: 5000, type: 'xp_total' },

  // Tâches vitales
  vital_first: { id: 'vital_first', name: 'Priorité santé', icon: '💊', description: 'Première tâche vitale complétée', requirement: 1, type: 'vital' },
  vital_10: { id: 'vital_10', name: 'Gardien de santé', icon: '🏥', description: '10 tâches vitales complétées', requirement: 10, type: 'vital' },
  vital_50: { id: 'vital_50', name: 'Champion du bien-être', icon: '💪', description: '50 tâches vitales complétées', requirement: 50, type: 'vital' },

  // Focus
  focus_first: { id: 'focus_first', name: 'Focus initié', icon: '🎯', description: 'Première session focus complétée', requirement: 1, type: 'focus' },
  focus_10: { id: 'focus_10', name: 'Concentré', icon: '🧘', description: '10 sessions focus complétées', requirement: 10, type: 'focus' },

  // Preuves
  proof_first: { id: 'proof_first', name: 'Preuve à l\'appui', icon: '📸', description: 'Première preuve ajoutée', requirement: 1, type: 'proof' },
  proof_10: { id: 'proof_10', name: 'Documentaliste', icon: '📚', description: '10 preuves ajoutées', requirement: 10, type: 'proof' },

  // Spéciaux
  early_bird: { id: 'early_bird', name: 'Lève-tôt', icon: '🌅', description: 'Tâche complétée avant 8h', requirement: 1, type: 'special' },
  night_owl: { id: 'night_owl', name: 'Couche-tard productif', icon: '🦉', description: 'Routine dodo complétée', requirement: 1, type: 'special' },
  perfect_day: { id: 'perfect_day', name: 'Journée parfaite', icon: '✨', description: '100% des tâches vitales en un jour', requirement: 1, type: 'special' },
};

// Configuration XP par action
export const XP_CONFIG = {
  START_TASK: 5,
  COMPLETE_SUBTASK: 3,
  COMPLETE_TASK: 10,
  VITAL_MULTIPLIER: 2,
  CHORE_BONUS: 5,
  STREAK_DAILY_BONUS: 20,
  FOCUS_SESSION: 15,
  PROOF_ADDED: 5,
  getDifficultyMultiplier: (duration) => {
    if (duration >= 120) return 2.0;
    if (duration >= 60) return 1.5;
    if (duration >= 30) return 1.2;
    return 1.0;
  }
};

// Messages d'encouragement contextuels
export const ENCOURAGEMENTS = {
  task_complete: [
    "Bravo ! Tu gères ! 💪",
    "Une de plus dans la poche ! ✨",
    "Tu es sur une lancée ! 🚀",
    "Excellent travail ! 🌟",
    "Continue comme ça ! 🔥"
  ],
  vital_complete: [
    "Tu prends soin de toi, c'est énorme ! 💊",
    "Ta santé te remercie ! 🏥",
    "Priorité #1 : check ! ✅",
    "Tu es responsable et fort·e ! 💪"
  ],
  streak_continue: [
    "Série en cours ! Ne lâche rien ! 🔥",
    "{streak} jours d'affilée ! Impressionnant ! ⭐",
    "Tu construis une habitude solide ! 💎"
  ],
  morning: [
    "Nouvelle journée, nouvelles victoires ! ☀️",
    "Tu es déjà debout, c'est un début ! 🌅",
    "Chaque petit pas compte aujourd'hui 👣"
  ],
  low_mood: [
    "Même les petites victoires comptent 💙",
    "Tu fais de ton mieux, et c'est suffisant 🌸",
    "Sois doux·ce avec toi-même aujourd'hui 🤗",
    "Une tâche à la fois, tu peux le faire 💪"
  ],
  badge_unlock: [
    "🎉 Nouveau badge débloqué : {badge} !",
    "Tu as gagné : {badge} ! Continue ! 🏆"
  ]
};

// Hook principal de gamification
export function useGameification() {
  const [gameData, setGameData] = useLocalStorage('adhd-planner-game', {
    totalXP: 0,
    todayXP: 0,
    lastXPDate: null,
    currentStreak: 0,
    longestStreak: 0,
    lastStreakDate: null,
    tasksCompleted: 0,
    vitalTasksCompleted: 0,
    focusSessionsCompleted: 0,
    proofsAdded: 0,
    unlockedBadges: [],
    recentXPGains: [],
    recentBadges: [],
    weeklyStats: {}
  });

  const [pendingAnimations, setPendingAnimations] = useState([]);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [showXPGain, setShowXPGain] = useState(false);
  const [lastXPGain, setLastXPGain] = useState(0);
  const [newBadge, setNewBadge] = useState(null);

  const level = useMemo(() => {
    const xp = gameData.totalXP;
    return Math.floor(Math.sqrt(xp / 50)) + 1;
  }, [gameData.totalXP]);

  const xpForNextLevel = useMemo(() => {
    return Math.pow(level, 2) * 50;
  }, [level]);

  const xpForCurrentLevel = useMemo(() => {
    return Math.pow(level - 1, 2) * 50;
  }, [level]);

  const levelProgress = useMemo(() => {
    const currentLevelXP = gameData.totalXP - xpForCurrentLevel;
    const levelRange = xpForNextLevel - xpForCurrentLevel;
    return Math.min((currentLevelXP / levelRange) * 100, 100);
  }, [gameData.totalXP, xpForCurrentLevel, xpForNextLevel]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    if (gameData.lastXPDate !== today) {
      setGameData(prev => ({
        ...prev,
        todayXP: 0,
        lastXPDate: today
      }));
    }
  }, [gameData.lastXPDate, setGameData]);

  const addXP = useCallback((amount, source = 'task') => {
    const prevLevel = Math.floor(Math.sqrt(gameData.totalXP / 50)) + 1;

    setGameData(prev => {
      const newTotalXP = prev.totalXP + amount;
      const newLevel = Math.floor(Math.sqrt(newTotalXP / 50)) + 1;

      if (newLevel > prevLevel) {
        setShowLevelUp(true);
        haptic.levelUp(); // Haptic feedback for level up
        setTimeout(() => setShowLevelUp(false), 3000);
      }

      return {
        ...prev,
        totalXP: newTotalXP,
        todayXP: prev.todayXP + amount,
        recentXPGains: [
          { amount, source, timestamp: Date.now() },
          ...prev.recentXPGains.slice(0, 9)
        ]
      };
    });

    setPendingAnimations(prev => [...prev, { type: 'xp', amount, id: Date.now() }]);
    setLastXPGain(amount);
    setShowXPGain(true);
    setTimeout(() => setShowXPGain(false), 2000);

    return amount;
  }, [gameData.totalXP, setGameData]);

  const completeTask = useCallback((task, isSubtask = false) => {
    let xp = isSubtask ? XP_CONFIG.COMPLETE_SUBTASK : XP_CONFIG.COMPLETE_TASK;

    if (task.category === 'vital') {
      xp *= XP_CONFIG.VITAL_MULTIPLIER;
    } else if (task.category === 'chore') {
      xp += XP_CONFIG.CHORE_BONUS;
    }

    if (task.duration && !isSubtask) {
      xp *= XP_CONFIG.getDifficultyMultiplier(task.duration);
    }

    if (task.points && !isSubtask) {
      xp = Math.max(xp, task.points);
    }

    xp = Math.round(xp);

    setGameData(prev => ({
      ...prev,
      tasksCompleted: prev.tasksCompleted + (isSubtask ? 0 : 1),
      vitalTasksCompleted: prev.vitalTasksCompleted + (task.category === 'vital' && !isSubtask ? 1 : 0)
    }));

    // Haptic feedback for task completion
    haptic.taskComplete();

    addXP(xp, task.category === 'vital' ? 'vital' : 'task');
    checkBadges();

    return xp;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addXP, setGameData]);

  const startTask = useCallback((task) => {
    haptic.tap();
    addXP(XP_CONFIG.START_TASK, 'start');
    return XP_CONFIG.START_TASK;
  }, [addXP]);

  const addProof = useCallback(() => {
    setGameData(prev => ({
      ...prev,
      proofsAdded: prev.proofsAdded + 1
    }));
    haptic.success();
    addXP(XP_CONFIG.PROOF_ADDED, 'proof');
    checkBadges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addXP, setGameData]);

  const completeFocusSession = useCallback(() => {
    setGameData(prev => ({
      ...prev,
      focusSessionsCompleted: prev.focusSessionsCompleted + 1
    }));
    haptic.focusEnd();
    addXP(XP_CONFIG.FOCUS_SESSION, 'focus');
    checkBadges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addXP, setGameData]);

  const updateStreak = useCallback((vitalsCompleted = false) => {
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

    setGameData(prev => {
      let newStreak = prev.currentStreak;

      if (vitalsCompleted) {
        if (prev.lastStreakDate === yesterday) {
          newStreak = prev.currentStreak + 1;
        } else if (prev.lastStreakDate !== today) {
          newStreak = 1;
        }

        if (newStreak > 0 && prev.lastStreakDate !== today) {
          haptic.streak(); // Haptic for streak milestone
          addXP(XP_CONFIG.STREAK_DAILY_BONUS, 'streak');
        }

        return {
          ...prev,
          currentStreak: newStreak,
          longestStreak: Math.max(prev.longestStreak, newStreak),
          lastStreakDate: today
        };
      }

      return prev;
    });

    checkBadges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addXP, setGameData]);

  const checkBadges = useCallback(() => {
    const newBadges = [];

    Object.values(BADGES).forEach(badge => {
      if (gameData.unlockedBadges.includes(badge.id)) return;

      let unlocked = false;

      switch (badge.type) {
        case 'streak':
          unlocked = gameData.currentStreak >= badge.requirement;
          break;
        case 'xp_total':
          unlocked = gameData.totalXP >= badge.requirement;
          break;
        case 'vital':
          unlocked = gameData.vitalTasksCompleted >= badge.requirement;
          break;
        case 'focus':
          unlocked = gameData.focusSessionsCompleted >= badge.requirement;
          break;
        case 'proof':
          unlocked = gameData.proofsAdded >= badge.requirement;
          break;
        default:
          break;
      }

      if (unlocked) {
        newBadges.push(badge);
      }
    });

    if (newBadges.length > 0) {
      setGameData(prev => ({
        ...prev,
        unlockedBadges: [...prev.unlockedBadges, ...newBadges.map(b => b.id)],
        recentBadges: [...newBadges, ...prev.recentBadges.slice(0, 4)]
      }));

      newBadges.forEach((badge, index) => {
        setTimeout(() => {
          haptic.badgeUnlock(); // Haptic for badge unlock
          setNewBadge(badge);
          setPendingAnimations(prev => [...prev, { type: 'badge', badge, id: Date.now() + Math.random() }]);
        }, index * 1500);
      });
    }
  }, [gameData, setGameData]);

  const clearXPGain = useCallback(() => setShowXPGain(false), []);
  const clearLevelUp = useCallback(() => setShowLevelUp(false), []);
  const clearNewBadge = useCallback(() => setNewBadge(null), []);

  const getEncouragement = useCallback((type = 'task_complete', context = {}) => {
    const messages = ENCOURAGEMENTS[type] || ENCOURAGEMENTS.task_complete;
    let message = messages[Math.floor(Math.random() * messages.length)];

    if (context.streak) {
      message = message.replace('{streak}', context.streak);
    }
    if (context.badge) {
      message = message.replace('{badge}', context.badge);
    }

    return message;
  }, []);

  const clearAnimation = useCallback((id) => {
    setPendingAnimations(prev => prev.filter(a => a.id !== id));
  }, []);

  const weeklyStats = useMemo(() => {
    const weekKey = getWeekKey();
    return gameData.weeklyStats[weekKey] || {
      tasksCompleted: 0,
      xpEarned: 0,
      vitalsCompleted: 0
    };
  }, [gameData.weeklyStats]);

  return {
    totalXP: gameData.totalXP,
    todayXP: gameData.todayXP,
    level,
    levelProgress,
    xpForNextLevel,
    currentStreak: gameData.currentStreak,
    longestStreak: gameData.longestStreak,
    unlockedBadges: gameData.unlockedBadges,
    tasksCompleted: gameData.tasksCompleted,
    vitalTasksCompleted: gameData.vitalTasksCompleted,
    pendingAnimations,
    showLevelUp,
    showXPGain,
    lastXPGain,
    newBadge,
    clearAnimation,
    clearXPGain,
    clearLevelUp,
    clearNewBadge,
    stats: gameData,
    addXP,
    completeTask,
    startTask,
    addProof,
    completeFocusSession,
    updateStreak,
    checkBadges,
    getEncouragement,
    BADGES,
    XP_CONFIG,
    weeklyStats
  };
}

function getWeekKey() {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
  return `${now.getFullYear()}-W${weekNumber}`;
}

export default useGameification;
